
import { cn } from "@/lib/utils";

export const getResponsivePipSizes = (pipCount: number, baseSize: "sm" | "md" | "lg") => {
  // For mobile (up to 768px), reduce sizes when pip count is high
  const isManyPips = pipCount > 7;
  
  if (isManyPips) {
    // Scale down for many pips
    return {
      sm: "w-5 h-5",      // Slightly smaller
      md: "w-6 h-6",      // Reduced from normal md
      lg: "w-7 h-7"       // Reduced from normal lg
    }[baseSize];
  }
  
  // Normal sizes for fewer pips
  return {
    sm: "w-5 h-5",
    md: "w-7 h-7", 
    lg: "w-9 h-9"
  }[baseSize];
};

export const getResponsiveRpgPipSizes = (pipCount: number, baseSize: "sm" | "md" | "lg") => {
  const isManyPips = pipCount > 7;
  
  if (isManyPips) {
    return {
      sm: "w-6 h-6",      // Slightly smaller than normal
      md: "w-7 h-7",      // Reduced from normal md
      lg: "w-8 h-8"       // Reduced from normal lg
    }[baseSize];
  }
  
  return {
    sm: "w-7 h-7",
    md: "w-9 h-9",
    lg: "w-11 h-11"
  }[baseSize];
};

export const getPipSizeClasses = (enableRpgMode: boolean) => ({
  sm: enableRpgMode ? "w-7 h-7" : "w-5 h-5",
  md: enableRpgMode ? "w-9 h-9" : "w-7 h-7",
  lg: enableRpgMode ? "w-11 h-11" : "w-9 h-9"
});

export const getResponsiveIconSizes = (pipCount: number, baseSize: "sm" | "md" | "lg") => {
  const isManyPips = pipCount > 7;
  
  if (isManyPips) {
    return {
      sm: "h-2.5 w-2.5",
      md: "h-3 w-3",
      lg: "h-3.5 w-3.5"
    }[baseSize];
  }
  
  return {
    sm: "h-3 w-3",
    md: "h-4 w-4",
    lg: "h-5 w-5"
  }[baseSize];
};

export const getIconSizeClasses = () => ({
  sm: "h-3 w-3",
  md: "h-4 w-4",
  lg: "h-5 w-5"
});

export const getResponsiveGapClasses = (pipCount: number, baseSize: "sm" | "md" | "lg") => {
  const isManyPips = pipCount > 7;
  
  if (isManyPips) {
    return {
      sm: "gap-1",
      md: "gap-1.5", 
      lg: "gap-2"
    }[baseSize];
  }
  
  return {
    sm: "gap-1.5",
    md: "gap-2.5",
    lg: "gap-3.5"
  }[baseSize];
};

export const getGapClasses = () => ({
  sm: "gap-1.5",
  md: "gap-2.5",
  lg: "gap-3.5"
});

export const getButtonSizeClasses = () => ({
  sm: "h-8 text-xs",
  md: "h-9",
  lg: "h-10"
});

export const getRpgPipClasses = (
  pipSizeClass: string,
  isActive: boolean,
  isHovered: boolean,
  darkMode: boolean,
  readonly: boolean,
  onPipClick?: (pipValue: number) => Promise<boolean>
) => {
  const baseClasses = cn(
    pipSizeClass,
    "relative overflow-hidden transition-all duration-500 ease-out flex items-center justify-center",
    "border-2 transform-gpu cursor-pointer",
    "rounded-lg bg-gradient-to-br",
    !readonly && onPipClick && "hover:scale-110 active:scale-95"
  );

  if (isActive) {
    return cn(
      baseClasses,
      "from-amber-400 via-amber-500 to-amber-600",
      "border-amber-300 text-amber-900",
      "shadow-lg shadow-amber-500/30",
      "ring-2 ring-amber-400/40",
      darkMode ? "ring-amber-400/50" : "ring-amber-400/60"
    );
  }

  return cn(
    baseClasses,
    "from-amber-900/30 to-amber-800/40 border-amber-600/50",
    darkMode 
      ? "text-amber-300 hover:from-amber-800/40 hover:to-amber-700/50" 
      : "text-amber-700 hover:from-amber-100/60 hover:to-amber-200/70",
    isHovered && "ring-1 ring-amber-400/30 scale-105"
  );
};

export const getStandardPipClasses = (
  pipSizeClass: string,
  isActive: boolean,
  isHovered: boolean,
  darkMode: boolean,
  readonly: boolean,
  onPipClick?: (pipValue: number) => Promise<boolean>
) => {
  const baseClasses = cn(
    "relative overflow-hidden transition-all duration-300 ease-out flex items-center justify-center",
    "transform-gpu cursor-pointer",
    !readonly && onPipClick && "hover:scale-105 active:scale-95"
  );

  if (isActive) {
    return cn(
      baseClasses,
      pipSizeClass,
      "bg-gradient-to-r from-purple-500 via-purple-600 to-purple-700",
      "text-white shadow-lg shadow-purple-500/25",
      "rounded-full border-2 border-purple-400",
      "ring-1 ring-purple-400/40"
    );
  }

  return cn(
    baseClasses,
    pipSizeClass,
    "rounded-full border-2 bg-gradient-to-r",
    darkMode 
      ? "from-gray-700 to-gray-600 border-gray-500 text-gray-300 hover:from-gray-600 hover:to-gray-500" 
      : "from-gray-100 to-gray-200 border-gray-300 text-gray-500 hover:from-gray-200 hover:to-gray-300",
    isHovered && "ring-1 ring-purple-400/30 scale-105"
  );
};
